<template>
  <Teleport to="body">
    <div
      v-if="currentMenu"
      class="js-global-menu fixed z-1010 rounded-md border border-border-subtle bg-surface shadow-sm"
      :class="rootClasses"
      :style="style">
      <component
        v-if="registry[currentMenu.name]"
        :is="registry[currentMenu.name]"
        v-bind="currentMenu.props"
        @close="close" />
    </div>
  </Teleport>
</template>

<script lang="ts">
import { useGlobalMenuStore } from '@/stores/globalMenuStore'
import { mapActions, mapState } from 'pinia'
import { useDismissablePriority } from '@team-milva/lab-components'

/**
 * Import all menu components that can be shown globally
 * (You can lazy-load these if you want)
 */
import AddReportBlockMenu from '@/components/menus/AddReportBlockMenu.vue'
import BcConnectionMenu from '@/components/menus/BcConnectionMenu.vue'
import BcDevToolsMenu from '@/components/menus/BcDevToolsMenu.vue'
import ColorPickerMenu from '@/components/menus/ColorPickerMenu.vue'
import DhlSettingsMenu from '@/components/menus/DhlSettingsMenu.vue'
import HeaderUserMenu from '@/components/menus/HeaderUserMenu.vue'
import IconPickerMenu from '@/components/menus/IconPickerMenu.vue'
import InvoicesPageMenu from '@/components/menus/InvoicesPageMenu.vue'
import OrderMenu from '@/components/menus/OrderMenu.vue'
import OrderResendReportsMenu from '@/components/menus/OrderResendReportsMenu.vue'
import OrderApproveOptionsMenu from '@/components/menus/OrderApproveOptionsMenu.vue'
import OrderApprovedDeliveryMenu from '@/components/menus/OrderApprovedDeliveryMenu.vue'
import OrderScheduledDeliveryMenu from '@/components/menus/OrderScheduledDeliveryMenu.vue'
import OrderSampleMenu from '@/components/menus/OrderSampleMenu.vue'
import OrderSampleAnalysisMenu from '@/components/menus/OrderSampleAnalysisMenu.vue'
import CustomerFilterMenu from '@/components/menus/CustomerFilterMenu.vue'
import CustomerUserRoleFilterMenu from '@/components/menus/CustomerUserRoleFilterMenu.vue'
import DesignSystemVerifiedFilterMenu from '@/components/menus/DesignSystemVerifiedFilterMenu.vue'
import LabEquipmentMaintenanceFilterMenu from '@/components/menus/LabEquipmentMaintenanceFilterMenu.vue'
import LabTranslationsFilterMenu from '@/components/menus/LabTranslationsFilterMenu.vue'
import LibraryVisibilityFilterMenu from '@/components/menus/LibraryVisibilityFilterMenu.vue'
import OrdersAnalysesFilterMenu from '@/components/menus/OrdersAnalysesFilterMenu.vue'
import OrdersDeliveriesFilterMenu from '@/components/menus/OrdersDeliveriesFilterMenu.vue'
import OrdersFilterMenu from '@/components/menus/OrdersFilterMenu.vue'
import OrdersColumnPickerMenu from '@/components/menus/OrdersColumnPickerMenu.vue'
import PaymentStatusMenu from '@/components/menus/PaymentStatusMenu.vue'
import PricingSegmentStateFilterMenu from '@/components/menus/PricingSegmentStateFilterMenu.vue'
import ReportAttachmentsFilterMenu from '@/components/menus/ReportAttachmentsFilterMenu.vue'
import StatusChipVariantPickerMenu from '@/components/menus/StatusChipVariantPickerMenu.vue'
import WidgetSettingsMenu from '@/components/menus/WidgetSettingsMenu.vue'

export default {
  name: 'GlobalMenuHost',
  data() {
    return {
      dismissable: useDismissablePriority(),
    }
  },
  computed: {
    ...mapState(useGlobalMenuStore, ['currentMenu']),

    /**
     * Global Menu registry:
     * key = menu name used by menu.open(name)
     * value = Vue component
     */
    registry(): Record<string, unknown> {
      return {
        AddReportBlockMenu,
        BcConnectionMenu,
        BcDevToolsMenu,
        ColorPickerMenu,
        CustomerFilterMenu,
        CustomerUserRoleFilterMenu,
        DesignSystemVerifiedFilterMenu,
        DhlSettingsMenu,
        LabEquipmentMaintenanceFilterMenu,
        LabTranslationsFilterMenu,
        LibraryVisibilityFilterMenu,
        HeaderUserMenu,
        IconPickerMenu,
        InvoicesPageMenu,
        OrderMenu,
        OrderResendReportsMenu,
        OrderApproveOptionsMenu,
        OrderApprovedDeliveryMenu,
        OrderScheduledDeliveryMenu,
        OrderSampleMenu,
        OrderSampleAnalysisMenu,
        OrdersAnalysesFilterMenu,
        OrdersColumnPickerMenu,
        OrdersDeliveriesFilterMenu,
        OrdersFilterMenu,
        PaymentStatusMenu,
        PricingSegmentStateFilterMenu,
        ReportAttachmentsFilterMenu,
        StatusChipVariantPickerMenu,
        WidgetSettingsMenu,
      }
    },
    rootClasses() {
      const rootClasses: string[] = []

      if (!this.currentMenu) {
        return rootClasses
      }

      // Set width
      if (this.currentMenu.width === 'dynamic') {
        rootClasses.push('w-auto')
      } else if (this.currentMenu.width === 'md') {
        rootClasses.push('w-70')
      }

      return rootClasses
    },
    style() {
      const style: { [key: string]: string } = {}

      if (!this.currentMenu) {
        return style
      }

      // Set position
      const { top, left, right, showAbove } = this.currentMenu.position

      style['top'] = `${top}px`

      if (showAbove) {
        style['transform'] = 'translateY(-100%)'
      }
      if (left) {
        style['left'] = `${left}px`
      }
      if (right) {
        style['right'] = `${right}px`
      }

      return style
    },
  },
  watch: {
    currentMenu(newMenu: unknown, oldMenu: unknown) {
      if (newMenu && !oldMenu) {
        this.dismissable.activate()
      } else if (!newMenu && oldMenu) {
        this.dismissable.deactivate()
      }
    },
  },
  methods: {
    ...mapActions(useGlobalMenuStore, ['close']),
    onKeydown(e: KeyboardEvent) {
      if (!this.currentMenu || e.key !== 'Escape') {
        return
      }

      // Capture-phase, mirrors ModalHost's own Escape handling — without
      // this, opening this menu from inside a modal (e.g. a widget's
      // settings menu inside "Tilpas dashboard") let the modal's own
      // capture-phase Escape handler close the modal first, orphaning this
      // menu on screen since nothing had told it to also close.
      if (!this.dismissable.isTop()) {
        return
      }

      e.preventDefault()
      e.stopPropagation()
      this.close()
    },
    windowClickListener(event: MouseEvent) {
      if (!this.currentMenu) {
        return
      }

      const target = event.target as HTMLElement
      const shouldIgnore =
        target.closest('.js-menu-trigger-wrap') ||
        target.closest('.js-global-menu')

      if (shouldIgnore) {
        return
      }

      this.close()
    },
    windowResizeListener() {
      if (this.currentMenu) {
        this.close()
      }
    },
    windowScrollListener(event: Event) {
      if (!this.currentMenu) {
        return
      }

      const target = event.target as HTMLElement | Document
      if (target instanceof HTMLElement && target.closest('.js-global-menu')) {
        return
      }

      this.close()
    },
  },
  mounted() {
    window.addEventListener('click', this.windowClickListener)
    window.addEventListener('resize', this.windowResizeListener)
    window.addEventListener('scroll', this.windowScrollListener, { capture: true })
    window.addEventListener('keydown', this.onKeydown, { passive: false, capture: true })
  },
  beforeUnmount() {
    window.removeEventListener('click', this.windowClickListener)
    window.removeEventListener('resize', this.windowResizeListener)
    window.removeEventListener('scroll', this.windowScrollListener, { capture: true })
    window.removeEventListener('keydown', this.onKeydown as EventListener, { capture: true })
  },
}
</script>

